import { ExportStatus, useExportStore } from '@/store/export-store'
import * as PIXI from 'pixi.js'
import Mp4Wasm from 'mp4-wasm'
import FFmpegUtility from '@/utils/ffmpeg-utility'
import GtmHelper from '@/analytics/gtm-helper'
import getPixiVideoControllers from '@/pixi/pixi-utility'
import { VideoController, Encoder, Mp4WasmEncoderConfig } from '@/pixi/types'
import { getEncoderConfig } from '@/utils/codec-support'

export class CanvasRecorder {
  private cancel = false
  private readonly mp4WasmLoaded = this.loadMp4Wasm()
  private readonly fps = 30
  private frameDurationMilliseconds = 1000/this.fps
  private frameDurationSeconds = 1/this.fps
  private videoControllers: VideoController[] = []
  private exportStore
  private exportStartedTime = 0
  private gtmHelper = GtmHelper.getInstance()

  constructor (
    private readonly ffmpegUtility: FFmpegUtility,
    private readonly ticker: PIXI.Ticker,
    private readonly pixiApplication: PIXI.Application
  ) {
    this.exportStore = useExportStore()
  }

  public cancelRecordCanvas (): void {
    this.cancel = true
    this.exportStartedTime = 0
  }

  public async startRecordCanvas (fileName: string, audioBinary: Uint8Array, untrimmedAudioDurationSeconds: number, loopStartTime: number, loopEndTime: number): Promise<void> {
    this.exportStartedTime = Date.now()
    this.exportStore.elapsedMilliseconds = 0
    this.videoControllers = getPixiVideoControllers(this.pixiApplication)
    return this.mp4WasmLoaded.then(async wasmBinary => {
      const mp4wasm = await new Mp4Wasm({
        wasmBinary
      })
      this.cancel = false
      const canvasContext = this.pixiApplication.view.getContext('webgl2')
      const isAudioTrimmed = untrimmedAudioDurationSeconds !== loopEndTime - loopStartTime
      const duration = isAudioTrimmed ? loopEndTime - loopStartTime : untrimmedAudioDurationSeconds
      if (canvasContext && duration) {
        const width = canvasContext.drawingBufferWidth
        const height = canvasContext.drawingBufferHeight
        const config = await getEncoderConfig(width, height, this.fps)
        const encoder = mp4wasm.createWebCodecsEncoder(config)
        this.initExportInfo(duration, 'mp4', fileName)
        this.pauseCanvas()
        return this.recordCanvas(encoder, duration, this.pixiApplication.view).then(async mp4 => {
          return this.ffmpegUtility.addAudioToMp4(fileName, mp4, audioBinary, loopStartTime, loopEndTime, this.fps, isAudioTrimmed).then(finalVideo => {
            this.exportStore.exportStatus = ExportStatus.COMPLETE
            this.downloadVideo(new Blob([finalVideo]), this.exportStore.fileName, config)
          })
        })
      }
    })
  }

  private async recordCanvas (encoder: Encoder,
    durationSeconds: number, canvas:HTMLCanvasElement): Promise<Uint8Array> {
    return new Promise<Uint8Array>( (resolve, reject) => {
      //Wait till canvas is fully loaded to avoid InvalidStateError when executing createImageBitmap (Refer sentry error VISUALIZER-8X)
      //requestAnimationFrame will only run when the browser is ready to repaint the window.
      //It'll ensure that the canvas element is fully rendered before the createImageBitmap function is called.
      window.requestAnimationFrame(() => {
        const recordFrames = async (time: number) => {
          Promise.all(this.seekVideos()).then( async () => {
            this.ticker.stop() // Somehow this seems to fix the sticker speed-up issue when deprioritizing tab
            this.ticker.update(time) // progress stickers
            this.pixiApplication?.render()
            this.exportStore.elapsedMilliseconds += this.frameDurationMilliseconds
            const bitmap = await createImageBitmap(canvas)
            await encoder.addFrame(bitmap)
            this.restartVideosIfRequired()
            if (this.exportStore.elapsedMilliseconds / 1000 >= durationSeconds) {
              encoder.end().then((mp4: Uint8Array) => {
                resolve(mp4)
              }).catch(e => reject(e))
              this.resumeCanvas()
            } else if (this.cancel) {
              this.resumeCanvas()
            } else {
              setTimeout(async () => await recordFrames(time + this.frameDurationMilliseconds), 0)
            }
          }).catch(e => reject(e))
        }
        recordFrames(Date.now())
      })
    })
  }

  private restartVideosIfRequired (): void {
    this.videoControllers.forEach(videoController => {
      if (videoController.currentTimeSeconds > videoController.video.duration) {
        videoController.currentTimeSeconds -= videoController.video.duration
      }
    })
  }

  private seekVideos (): Promise<void>[] {
    return this.videoControllers.map(videoController => {
      videoController.seekPromise = this.initVideoSeekedPromise(videoController.video)
      videoController.video.currentTime = videoController.currentTimeSeconds
      videoController.currentTimeSeconds += this.frameDurationSeconds
      return videoController.seekPromise.then(() => videoController.texture.update())
    })
  }

  private resumeCanvas (): void {
    this.ticker.start()
    this.videoControllers.forEach(videoController => {
      videoController.video.play()
    })
  }

  private pauseCanvas (): void {
    this.videoControllers.forEach(videoController => {
      videoController.video.pause()
    })
    this.ticker.stop()
  }

  private async loadMp4Wasm (): Promise<ArrayBuffer> {
    const resp = await fetch('./mp4-wasm/mp4.wasm')
    return await resp.arrayBuffer()
  }

  private initVideoSeekedPromise (video: HTMLVideoElement): Promise<void> {
    return new Promise<void>(resolve => {
      if (video) {
        video.onseeked = () => {
          resolve()
        }
      }
    })
  }

  private downloadVideo (blob: Blob, fileName: string, config: Mp4WasmEncoderConfig): void {
    this.exportStore.downloadLink = URL.createObjectURL(blob)
    const temporaryButton = <HTMLAnchorElement>document.createElement('a')
    temporaryButton.href = this.exportStore.downloadLink
    temporaryButton.download = fileName
    temporaryButton.click()

    this.gtmHelper.triggerDownloadGtmEvents(blob.size, Date.now() - this.exportStartedTime, config)
  }

  private initExportInfo (duration: number, format: string, audioFileName: string): void {
    this.exportStore.durationMilliseconds = duration * 1000,
    this.exportStore.fileFormat = format,
    this.exportStore.elapsedMilliseconds = 0,
    this.exportStore.exportStatus = ExportStatus.EXPORTING
    this.exportStore.fileName = audioFileName.replace(/\.[^/.]+$/, '.') + format
  }
}
